import { useState, useRef, useEffect } from 'react';
import { refreshMonsterMapping } from '../utils/monsterMapping';

export function useMonsterManagement(monsterMapping: any, setMonsterMapping: (data: any) => void) {
  const [adminAttr, setAdminAttr] = useState('Water');
  const [adminStar, setAdminStar] = useState(1);
  const [managePopup, setManagePopup] = useState<{ name: string, info: any, attribute: string } | null>(null);
  const [manageAwakeState, setManageAwakeState] = useState<'Unawaken'|'Awakened'|'SecondAwakened'>('Awakened');
  // mobImgVersion: 관리 팝업에서만 사용. 이미지 업로드/삭제 후에만 갱신
  const [mobImgVersion, setMobImgVersion] = useState(1);
  const prevPopupName = useRef<string | null>(null);
  const [nameEditMode, setNameEditMode] = useState(false);
  const [nameEditValue, setNameEditValue] = useState('');
  const [aliasEdit, setAliasEdit] = useState<Record<string, string>>({});
  const [aliasSaving, setAliasSaving] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showAliasDropdown, setShowAliasDropdown] = useState(false);
  const [showCollabSelect, setShowCollabSelect] = useState(false);

  useEffect(() => {
    if (managePopup && (managePopup.name + managePopup.attribute) !== prevPopupName.current) {
      // if (managePopup.attribute) setAdminAttr(managePopup.attribute);
      const info = managePopup.info;
      if (
        info &&
        info[adminAttr] &&
        info[adminAttr].SecondAwakened &&
        Object.keys(info[adminAttr].SecondAwakened).length > 0
      ) {
        setManageAwakeState('SecondAwakened');
      } else {
        setManageAwakeState('Awakened');
      }
      prevPopupName.current = managePopup.name + managePopup.attribute;
    }
    if (!managePopup) {
      prevPopupName.current = null;
    }
  }, [managePopup]);

  useEffect(() => {
    if (managePopup && managePopup.name) {
      refreshMonsterMapping()
        .then(data => {
          if (data && data.monsters && data.monsters[managePopup.name]) {
            setManagePopup(mp => mp ? { ...mp, info: data.monsters[managePopup.name] } : mp);
          }
        });
    }
  }, [managePopup && managePopup.name]);

  // 별칭 추가
  const handleAliasAdd = async (monsterName: string) => {
    const newAlias = (aliasEdit[monsterName] || '').trim();
    if (!newAlias) return;
    setAliasSaving(monsterName);
    const prevAliases = monsterMapping.monsters[monsterName]?.[adminAttr]?.aliases || [];
    if (prevAliases.includes(newAlias)) {
      setAliasSaving(null);
      setAliasEdit({ ...aliasEdit, [monsterName]: '' });
      return;
    }
    const newAliases = [...prevAliases, newAlias];
    try {
      const res = await fetch('/api/monsters/monster-alias', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: monsterName, attribute: adminAttr, aliases: newAliases }),
        credentials: 'include'
      });
      if (res.ok) {
        setMonsterMapping((prev: any) => {
          if (!prev) return prev;
          const copy = { ...prev };
          if (copy.monsters[monsterName] && copy.monsters[monsterName][adminAttr]) {
            copy.monsters[monsterName][adminAttr].aliases = newAliases;
          }
          return copy;
        });
        setAliasEdit({ ...aliasEdit, [monsterName]: '' });
        const mappingData = await refreshMonsterMapping();
        setMonsterMapping(mappingData);
        setManagePopup((mp: any) => mp && mp.name === monsterName ? { ...mp, info: mappingData.monsters[monsterName] } : mp);
      } else {
        alert('추가 실패');
      }
    } catch {
      alert('추가 실패');
    }
    setAliasSaving(null);
  };

  // 별칭 삭제
  const handleAliasDelete = async (monsterName: string, aliasToDelete: string, attribute: string) => {
    setAliasSaving(monsterName + aliasToDelete);
    const prevAliases = monsterMapping.monsters[monsterName]?.[attribute]?.aliases || [];
    const newAliases = prevAliases.filter((a: string) => a !== aliasToDelete);
    try {
      const res = await fetch('/api/monsters/monster-alias', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: monsterName, attribute, aliases: newAliases }),
        credentials: 'include'
      });
      if (res.ok) {
        setMonsterMapping((prev: any) => {
          if (!prev) return prev;
          const copy = { ...prev };
          if (copy.monsters[monsterName] && copy.monsters[monsterName][attribute]) {
            copy.monsters[monsterName][attribute].aliases = newAliases;
          }
          return copy;
        });
      } else {
        alert('권한이 없습니다.');
      }
    } catch {
      alert('권한이 없습니다.');
    }
    setAliasSaving(null);
  };



  // 이름 변경
  const handleNameEditStart = () => {
    if (!managePopup) return;
    setNameEditValue(managePopup.name || '');
    setNameEditMode(true);
  };
  const handleNameEditCancel = () => {
    setNameEditMode(false);
  };
  const handleNameEditSave = async () => {
    if (!managePopup) return;
    const oldName = managePopup.name;
    const newName = nameEditValue.trim();
    if (!newName || newName === oldName) {
      setNameEditMode(false);
      return;
    }
    try {
      const res = await fetch('/api/monsters/rename-monster', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldName, newName }),
        credentials: 'include'
      });
      if (res.ok) {
        refreshMonsterMapping()
          .then(data => {
            setMonsterMapping(data);
            setManagePopup((mp: any) => mp ? { ...mp, name: newName, info: data.monsters[newName] } : mp);
          });
      } else {
        alert('권한이 없습니다.');
      }
    } catch {
      alert('권한이 없습니다.');
    }
    setNameEditMode(false);
  };

  // 각성상태 변경
  const handleAwakeStateChange = async (state: typeof manageAwakeState) => {
    setManageAwakeState(state as 'Unawaken'|'Awakened'|'SecondAwakened');
    if (managePopup && managePopup.name) {
      const mappingData = await refreshMonsterMapping();
      setMonsterMapping(mappingData);
      setManagePopup((mp: any) => mp && mp.name === managePopup.name ? { ...mp, info: mappingData.monsters[managePopup.name] } : mp);
    }
  };

  // 별칭 전체 추가
  const handleAliasAddAll = async (monsterName: string) => {
    const newAlias = (aliasEdit[monsterName] || '').trim();
    if (!newAlias) return;
    setAliasSaving(monsterName + '_all');
    try {
      const res = await fetch('/api/monsters/monster-alias-all', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: monsterName, alias: newAlias }),
        credentials: 'include'
      });
      if (res.ok) {
        setAliasEdit({ ...aliasEdit, [monsterName]: '' });
        const mappingData = await refreshMonsterMapping();
        setMonsterMapping(mappingData);
        setManagePopup((mp: any) => mp && mp.name === monsterName ? { ...mp, info: mappingData.monsters[monsterName] } : mp);
      } else {
        alert('권한이 없습니다.');
      }
    } catch {
      alert('권한이 없습니다.');
    }
    setAliasSaving(null);
  };

  // 클립보드 이미지 붙여넣기


  // 콜라보몹 연동 핸들러
  const handleSelectCollab = async (mob: { name: string }) => {
    if (!managePopup?.name) return;
    try {
      const res = await fetch('/api/monsters/set-collab', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: managePopup.name, collabName: mob.name })
      });
      if (!res.ok) {
        alert('권한이 없거나 오류');
        return;
      }
      const mappingData = await refreshMonsterMapping();
      setMonsterMapping(mappingData);
      setManagePopup(mp => mp ? { ...mp, info: mappingData.monsters[managePopup.name] } : mp);
    } catch (e) {
      alert('오류');
    }
  };

  /**
   * 지금 열어둔 몹을 **콜라보 쪽**으로 지정한다.
   *
   * 목록은 콜라보 쪽 이름으로 한 칸만 그린다(원본까지 그리면 같은 몹이 두 개로 보인다).
   * 어느 쪽이 콜라보인지는 데이터로 알 수 없어서(API 에 콜라보 정보가 없다) 사람이 정한다.
   */
  const handleMarkCollabSide = async (target?: string) => {
    if (!managePopup?.name) return;
    // 대상을 주면 그 몹을 콜라보로 만든다 — 짝 이름을 넘기면 서로 바뀐다(토글).
    const name = target || managePopup.name;
    try {
      const res = await fetch('/api/monsters/set-collab-side', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      if (!res.ok) {
        const m = await res.json().catch(() => ({}));
        alert(m.error || '권한이 없거나 오류');
        return;
      }
      const mappingData = await refreshMonsterMapping();
      setMonsterMapping(mappingData);
      setManagePopup(mp => mp ? { ...mp, info: mappingData.monsters[managePopup.name] } : mp);
    } catch (e) {
      alert('오류');
    }
  };

  // 콜라보몹 해제 핸들러
  const handleRemoveCollab = async () => {
    if (!managePopup?.name) return;
    try {
      const res = await fetch('/api/monsters/set-collab', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: managePopup.name, collabName: null })
      });
      if (!res.ok) {
        alert('권한이 없거나 오류');
        return;
      }
      const mappingData = await refreshMonsterMapping();
      setMonsterMapping(mappingData);
      setManagePopup(mp => mp ? { ...mp, info: mappingData.monsters[managePopup.name] } : mp);
    } catch (e) {
      alert('오류');
    }
  };

  // 디스플레이 이름 가져오기 (한글 이름 중 가장 짧은 것, 없으면 aliases[0])
  const getDisplayName = (monsterName: string, attribute: string) => {
    if (!monsterMapping?.monsters?.[monsterName]?.[attribute]?.aliases) {
      return monsterName;
    }
    
    const aliases = monsterMapping.monsters[monsterName][attribute].aliases;
    if (!aliases || aliases.length === 0) {
      return monsterName;
    }
    
    // 한글 이름들만 필터링 (한글 문자가 포함된 이름)
    const koreanNames = aliases.filter((alias: string) => /[가-힣]/.test(alias));
    
    if (koreanNames.length > 0) {
      // 한글 이름 중 가장 짧은 것 반환
      return koreanNames.reduce((shortest: string, current: string) => 
        current.length < shortest.length ? current : shortest
      );
    }
    
    // 한글 이름이 없으면 aliases[0] 반환
    return aliases[0];
  };

  return {
    monsterMapping, setMonsterMapping,
    adminAttr, setAdminAttr,
    adminStar, setAdminStar,
    managePopup, setManagePopup,
    manageAwakeState, setManageAwakeState,
    mobImgVersion, setMobImgVersion,
    nameEditMode, setNameEditMode,
    nameEditValue, setNameEditValue,
    aliasEdit, setAliasEdit,
    aliasSaving, setAliasSaving,
    search, setSearch,
    showAliasDropdown, setShowAliasDropdown,
    showCollabSelect, setShowCollabSelect,
    handleAliasAdd,
    handleAliasDelete,
    handleNameEditStart,
    handleNameEditCancel,
    handleNameEditSave,
    handleAwakeStateChange,
    handleAliasAddAll,
    handleSelectCollab,
    handleMarkCollabSide,
    handleRemoveCollab,
    getDisplayName,
  };
} 